import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import csvToJson from 'convert-csv-to-json';

import { EmptyState } from '@/components/EmptyState';
import Loader from '@/components/Loader';
import TimeRangeButtons from '@/components/TimeRangeButtons/TimeRangeButtons';
import type { TimeRangeButton } from '@/constants/constants';
import { TABLE_ROW_HEIGHT_PX } from '@/constants/constants';
import { useAppSelector } from '@/hooks/redux-hooks';
import useCsvDownload from '@/hooks/useCsvDownload';
import downloadTextFileFromDatalake from '@/utils/downloadTextFileFromDatalake';
import { parseAndFormatUserDateString } from '@/utils/parseUserDateString';
import { queryApiByIdString } from '@/utils/queryApi';

function parseCsvBetter(text: string) {
  return csvToJson
    .formatValueByType(true)
    .supportQuotedField(true)
    .fieldDelimiter(',')
    .csvStringToJson(text);
}
export type CSVTableMetadata = {
  queryFileId?: string;
  timeRangeButtons?: TimeRangeButton[];
  align?: {
    [key: string]: 'left' | 'right' | 'center';
  };
};
const CsvTableWidget = ({
  fileName,
  groupName,
  metadata,
  layout
}: {
  fileName: string;
  groupName: string;
  layout?: { fullWidth?: boolean; height?: number };
  metadata?: CSVTableMetadata;
}) => {
  const [from, setFrom] = useState<string | undefined>(undefined);

  const [activeButtonIndex, setActiveButtonIndex] = useState(0);
  const init = useRef(false);

  const timeRangeButtons = useMemo(() => metadata?.timeRangeButtons || [], [metadata]);

  useEffect(() => {
    if (init.current || timeRangeButtons.length < 1) return;
    const index = timeRangeButtons.findIndex(bt => bt.default);

    if (index > -1) {
      setActiveButtonIndex(index);
      setFrom(timeRangeButtons[index].value);
      init.current = true;
    }
  }, [timeRangeButtons]);

  const queryFileId = metadata?.queryFileId;
  useCsvDownload(from, queryFileId);

  const { userConfig } = useAppSelector(state => state.userConfig);
  const { data, isLoading, error } = useQuery({
    queryKey: ['csv-table', { fileName, groupName }, from, queryFileId],
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (queryFileId) {
        return queryApiByIdString(queryFileId, from);
      }
      return downloadTextFileFromDatalake(fileName, groupName);
    }
  });
  const rows = data ? parseCsvBetter(data) : [];
  const header: string[] = useMemo(() => {
    if (rows.length === 0) return [];
    const first = rows[0];
    // If parser returns array of objects, use object keys as header
    if (first && typeof first === 'object' && !Array.isArray(first)) {
      return Object.keys(first);
    }

    const firstRow = Array.isArray(rows[0]) ? (rows[0] as unknown[]) : [];
    return firstRow.map(String);
  }, [rows]);

  const dataRows: unknown[] = useMemo(() => {
    if (rows.length === 0) return [];
    const first = rows[0];
    const isObjectRows = first && typeof first === 'object' && !Array.isArray(first);
    if (isObjectRows) return rows; // parser already used header row
    return rows.slice(1);
  }, [rows]);

  const theme = useTheme();

  if (isLoading) return <Loader />;

  return (
    <Paper>
      {timeRangeButtons.length > 0 && (
        <TimeRangeButtons
          activeButtonIndex={activeButtonIndex}
          setActiveButtonIndex={setActiveButtonIndex}
          timeRangeButtons={timeRangeButtons}
          onButtonClick={setFrom}
        />
      )}
      {error || !rows.length ? (
        <EmptyState />
      ) : (
        <TableContainer
          sx={{
            maxHeight: layout?.height
              ? `${layout?.height * TABLE_ROW_HEIGHT_PX + 47}px`
              : 'fit-content',
            pb: 2,
            borderCollapse: 'separate',
            borderSpacing: 0
          }}
        >
          <Table
            stickyHeader
            size='small'
            sx={{ tableLayout: 'auto' }}
          >
            {header.length > 0 && (
              <TableHead
                sx={{
                  '& th': {
                    px: 1.5,
                    pb: 1,
                    borderBottom: `0.5px solid ${theme.palette.text.primary}`,
                    backgroundColor: 'background.paper'
                  }
                }}
              >
                <TableRow>
                  {header.map((h, i) => (
                    <TableCell
                      key={i}
                      align={metadata?.align?.[h] ?? 'left'}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {dataRows.map((r, ri) => (
                <TableRow
                  key={ri}
                  sx={{
                    '&:nth-child(1) td': {
                      pt: 1
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: '#121836'
                    },
                    '& td': {
                      px: 1.5
                    }
                  }}
                >
                  {(() => {
                    const formatMaybeDate = (value: unknown) => {
                      if (typeof value === 'string' && value.length <= 50) {
                        const { formatted, matched } = parseAndFormatUserDateString(
                          value,
                          userConfig
                        );
                        if (matched) return formatted;
                      }
                      return String(value ?? '');
                    };

                    if (r && typeof r === 'object' && !Array.isArray(r)) {
                      const obj = r as Record<string, unknown>;
                      return header.map((h, ci) => (
                        <TableCell
                          key={ci}
                          align={metadata?.align?.[h] ?? 'left'}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {formatMaybeDate(obj[h])}
                        </TableCell>
                      ));
                    }
                    if (Array.isArray(r)) {
                      return (r as unknown[]).map((val, ci) => {
                        const colName = header[ci];
                        return (
                          <TableCell
                            key={ci}
                            align={metadata?.align?.[colName] ?? 'left'}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {formatMaybeDate(val)}
                          </TableCell>
                        );
                      });
                    }
                    return null;
                  })()}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default CsvTableWidget;
